import { useContext } from 'react';
import { FormContext } from '@frontend/components/contexts/form/formContext';
import type { FormResult, FormState } from './useForm';

/**
 * useFormContext Hook
 *
 * This custom hook is used to access the form context provided by the FormProvider component.
 * It allows you to access form state and form handlers in any component that is a child of FormProvider.
 *
 * Type parameter:
 * - T: A type that extends FormState. This should be an object type where each key represents a field in the form.
 *
 * Returns:
 * - An object of type FormResult<T> that includes the form state and form handlers.
 *
 * Example usage:
 *
 * ```tsx
 * const form = useFormContext<MyFormState>();
 * ```
 *
 * In this example, `MyFormState` would be an object type that represents the state of your form. The `form` variable
 * would then be an object that includes the form state and form handlers.
 */
export function useFormContext<T extends FormState>() {
    return useContext<FormResult<T>>(FormContext);
}
